import { ChangeEvent, ReactElement } from 'react';
import {
  CloseIcon,
  DocumentFileIcon,
  ImageFileIcon,
  UploadIcon,
  WarningIcon,
} from '../../../assets/icons';
import { useIsDesktop } from '../../hooks';
import { tokens, visuallyHiddenStyle } from '../../theme';

export type UploadedFile = { id: string; file: File };

// Gaps, paddings e tamanhos de ícone fixos nos dois breakpoints no Figma (não fluidos).
const HEADER_GAP = 8;
const SECTION_GAP = 16;
const FIELD_GAP = 8;
const LIST_GAP = 8;
const BUTTON_HEIGHT = 40;
const BUTTON_PADDING = '8px 8px 8px 12px';
const BUTTON_ICON_GAP = 8;
const BUTTON_FONT_SIZE = tokens.typography.fontSize.button;
const DESKTOP_BUTTON_WIDTH = 246;
const DESKTOP_ITEM_PADDING = 12;
const ITEM_GAP = 8;
const FILE_INFO_GAP = 4;
const FILE_ICON_SIZE = 16;
const REMOVE_ICON_SIZE = 16;
const ALERT_PADDING = '8px 12px';
const ALERT_ICON_GAP = 8;
const ALERT_ICON_SIZE = 20;

function formatFileSize(bytes: number): string {
  return `${(bytes / 1024).toFixed(2)} KB`;
}

type FileUploadFieldProps = {
  id: string;
  label: string;
  instructions: string;
  files: UploadedFile[];
  maxFiles: number;
  accept: string;
  error?: string;
  onAddFiles: (fileList: FileList) => void;
  onRemoveFile: (id: string) => void;
};

export const FileUploadField = ({
  id,
  label,
  instructions,
  files,
  maxFiles,
  accept,
  error,
  onAddFiles,
  onRemoveFile,
}: FileUploadFieldProps): ReactElement => {
  const isDesktop = useIsDesktop();
  const isFull = files.length >= maxFiles;
  const errorId = error ? `${id}-error` : undefined;
  const instructionsId = `${id}-instructions`;

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files) {
      onAddFiles(event.target.files);
    }
    event.target.value = '';
  };

  return (
    <div style={styles.section}>
      <div style={styles.header}>
        <p style={styles.label}>{label}</p>
        <span style={styles.optionalTag}>Opcional</span>
      </div>
      <div style={styles.field}>
        <label
          htmlFor={id}
          style={{
            ...styles.button,
            width: isDesktop ? DESKTOP_BUTTON_WIDTH : '100%',
            cursor: isFull ? 'not-allowed' : 'pointer',
            opacity: isFull ? 0.6 : 1,
          }}
        >
          <span>Clique para fazer upload</span>
          <UploadIcon width={20} height={20} aria-hidden="true" focusable="false" />
        </label>
        <input
          id={id}
          type="file"
          accept={accept}
          multiple
          disabled={isFull}
          aria-describedby={[instructionsId, errorId].filter(Boolean).join(' ')}
          onChange={handleChange}
          style={styles.hiddenInput}
        />
        <p id={instructionsId} style={styles.instructions}>
          {instructions}
        </p>
        {error && (
          <div id={errorId} role="alert" style={styles.alert}>
            <WarningIcon
              width={ALERT_ICON_SIZE}
              height={ALERT_ICON_SIZE}
              color={tokens.colors.brand.orange.light150}
              aria-hidden="true"
              focusable="false"
            />
            <p style={styles.alertText}>{error}</p>
          </div>
        )}
        {/* Sempre presente no DOM (mesmo vazio) para que a troca de texto seja anunciada — um elemento
            aria-live só recém-montado costuma não ser lido pelo leitor de tela na primeira aparição. */}
        <p aria-live="polite" style={styles.visuallyHidden}>
          {files.length > 0 ? `${files.length} arquivo(s) anexado(s)` : ''}
        </p>
        {files.length > 0 && (
          <ul aria-label="Arquivos anexados" style={styles.list}>
            {files.map(({ id: fileId, file }) => {
              const isImage = file.type.startsWith('image/');
              return (
                <li
                  key={fileId}
                  style={{ ...styles.item, padding: isDesktop ? DESKTOP_ITEM_PADDING : 0 }}
                >
                  {isImage ? (
                    <ImageFileIcon
                      width={FILE_ICON_SIZE}
                      height={FILE_ICON_SIZE}
                      color={tokens.colors.brand.gray.light150}
                      aria-hidden="true"
                      focusable="false"
                    />
                  ) : (
                    <DocumentFileIcon
                      width={FILE_ICON_SIZE}
                      height={FILE_ICON_SIZE}
                      color={tokens.colors.brand.gray.light150}
                      aria-hidden="true"
                      focusable="false"
                    />
                  )}
                  <div style={styles.fileInfo}>
                    <span style={styles.fileName}>{file.name}</span>
                    <span style={styles.fileSize}>{formatFileSize(file.size)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveFile(fileId)}
                    aria-label={`Remover ${file.name}`}
                    style={styles.removeButton}
                  >
                    <CloseIcon
                      width={REMOVE_ICON_SIZE}
                      height={REMOVE_ICON_SIZE}
                      color={tokens.colors.brand.gray.light150}
                      aria-hidden="true"
                      focusable="false"
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: SECTION_GAP,
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: HEADER_GAP,
    width: '100%',
  },
  label: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.sectionTitle,
    fontWeight: tokens.typography.fontWeight.semiBold,
    color: tokens.colors.brand.gray.DEFAULT,
    textTransform: 'uppercase',
  },
  optionalTag: {
    fontSize: tokens.typography.fontSize.disclaimer,
    color: tokens.colors.brand.gray.DEFAULT,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: FIELD_GAP,
    width: '100%',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: BUTTON_ICON_GAP,
    height: BUTTON_HEIGHT,
    boxSizing: 'border-box',
    padding: BUTTON_PADDING,
    borderRadius: tokens.radius.small,
    backgroundColor: tokens.colors.brand.green.DEFAULT,
    color: tokens.colors.global.white,
    fontSize: BUTTON_FONT_SIZE,
    fontWeight: tokens.typography.fontWeight.medium,
  },
  hiddenInput: visuallyHiddenStyle,
  visuallyHidden: {
    ...visuallyHiddenStyle,
    margin: tokens.spacing.none,
  },
  instructions: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.instruction,
    color: tokens.colors.brand.gray.light150,
  },
  alert: {
    display: 'flex',
    alignItems: 'center',
    gap: ALERT_ICON_GAP,
    width: '100%',
    boxSizing: 'border-box',
    padding: ALERT_PADDING,
    border: `1px solid ${tokens.colors.brand.orange.light150}`,
    borderRadius: tokens.radius.small,
    backgroundColor: tokens.colors.brand.orange.light950,
  },
  alertText: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.disclaimer,
    color: tokens.colors.brand.orange.dark150,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: LIST_GAP,
    width: '100%',
    margin: tokens.spacing.none,
    padding: tokens.spacing.none,
    listStyle: 'none',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: ITEM_GAP,
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: tokens.colors.global.white,
    borderRadius: tokens.radius.small,
  },
  fileInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: FILE_INFO_GAP,
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.typography.fontSize.input,
    color: tokens.colors.global.black,
  },
  fileSize: {
    fontSize: tokens.typography.fontSize.disclaimer,
    color: tokens.colors.brand.gray.light150,
  },
  removeButton: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
    padding: tokens.spacing.none,
    cursor: 'pointer',
  },
};
